import { IconLibraryPhoto } from "@tabler/icons-react";
import { useMemo } from "react";

import { useAddLeagueNamingKey, useAddTournamentNamingKey } from "@/hooks";
import { MessageKey } from "@/i18n";
import { LeagueOnboardingStepId } from "@/types";
import {
  IconBarGraph,
  IconFloatingQuestion,
  IconOrganization,
  IconTrophy,
  IconUsers,
  InputIcon,
} from "@/ui";

export type LeagueOnboardingStepMeta = {
  title: MessageKey;
  description: MessageKey;
  icon: InputIcon;
  /**
   * Deep-link path RELATIVE to `/league/:id/`. Verified against the league
   * sidebar nav model (`useLeagueNavGroups`): each value is the real `path`
   * (or `edit/<editTabId>`) the matching nav item navigates to. The container
   * prepends `/league/:id/` and scopes it via `useScopedHref`.
   */
  path: string;
};

/**
 * Presentation + deep-link metadata for each league onboarding step, in canonical
 * guide order. Completion state is owned by the backend
 * (`GET /leagues/:id/onboarding`) and merged in by the container. Shared by the
 * dedicated page (`OnboardingStepsGrid`) and the overview card (`OnboardingCard`).
 *
 * A hook rather than a const because the copy names both the container ("your
 * tour" vs "your league") and the tournament term ("Tournament Categories" vs
 * "Stage Categories"), which only resolve against the active
 * `LeagueNamingContext` — both league onboarding surfaces mount
 * `CurrentLeagueNamingProvider`.
 */
export const useLeagueOnboardingStepMeta = (): {
  [key in LeagueOnboardingStepId]: LeagueOnboardingStepMeta;
} => {
  const addLeagueNamingKey = useAddLeagueNamingKey();
  const addTournamentNamingKey = useAddTournamentNamingKey();

  return useMemo(
    () => ({
      "general-setup": {
        title: "league.pages.details.onboarding.generalSetup.title",
        description: "league.pages.details.onboarding.generalSetup.description",
        icon: IconOrganization,
        path: "edit/general",
      },
      team: {
        title: "league.pages.details.onboarding.team.title",
        description: addLeagueNamingKey("league.pages.details.onboarding.team.description"),
        icon: IconUsers,
        path: "settings/team",
      },
      "fact-sheet": {
        title: "league.pages.details.onboarding.factSheet.title",
        description: "league.pages.details.onboarding.factSheet.description",
        icon: IconFloatingQuestion,
        path: "edit/factSheet",
      },
      "tournament-categories": {
        title: addTournamentNamingKey("league.pages.details.onboarding.tournamentCategories.title"),
        description: addLeagueNamingKey(
          "league.pages.details.onboarding.tournamentCategories.description"
        ),
        icon: IconBarGraph,
        path: "edit/tournamentCategories",
      },
      ranking: {
        title: "league.pages.details.onboarding.ranking.title",
        description: "league.pages.details.onboarding.ranking.description",
        icon: IconTrophy,
        path: "edit/rankingCategories",
      },
      content: {
        title: "league.pages.details.onboarding.content.title",
        description: addLeagueNamingKey("league.pages.details.onboarding.content.description"),
        icon: IconLibraryPhoto,
        path: "news",
      },
    }),
    [addLeagueNamingKey, addTournamentNamingKey]
  );
};

/** League onboarding steps in canonical guide order. */
export const LEAGUE_ONBOARDING_STEP_ORDER: LeagueOnboardingStepId[] = [
  "general-setup",
  "team",
  "fact-sheet",
  "tournament-categories",
  "ranking",
  "content",
];
